// Turning a source spec into a POST-able draft definition.
//
// This is the whole reason the simple form can exist. The advanced editor hands the operator the
// raw definition and expects them to know which of its keys the create endpoint still accepts;
// here nobody sees the definition at all, so this module has to make every rewrite the endpoint
// requires — and then say, in words, what it did.
//
// Three groups of change, and they are not the same kind of thing:
//
//   1. LEGACY REWRITES. Every one of the 29 seed specs is written in a shape create now rejects
//      (`enabled_in`, `score_jsonpath`, and three of them both output contracts at once). These
//      are mechanical and safe — the same removals `scoreSpec/payload.js` makes on the advanced
//      editor's way out, plus the two the advanced editor deliberately leaves to the operator
//      because they need a decision there. Here there is no operator to ask: a duplicate keeps
//      the source's behaviour, and for a spec declaring both contracts `output_schema` is the one
//      the engine enforces, so the prose one is the one that goes.
//
//   2. FORCED BY THE NEW PROMPT. A different prompt is a different signal: new id, new version,
//      new description, a provenance note, and — the easy one to miss — the source's
//      `feed_name`s cleared. Keeping a feed name passes create AND lock and then 409s at approve,
//      by which time the spec is frozen and there is no unlock endpoint.
//
//   3. NOTHING ELSE. trigger, document_set, model, budgets, score names, jsonpaths, output_schema
//      and extra_columns all cross verbatim. The form does not expose them and this does not
//      touch them.
//
// It does not throw on an unduplicatable source (the doc's reference implementation does). The
// preview panel calls this on every keystroke during render, so a source with no output contract
// has to come back as a value the form can display, not an exception the form has to catch.

// Set by the server on the RECORD. `record.spec_json` should never carry them, but a caller that
// flattened the record would send them and eat an immediate 422 naming all ten.
const OPERATIONAL_KEYS = [
  'status',
  'source',
  'live_enabled',
  'created_by',
  'approved_by',
  'created_at',
  'locked_at',
  'approved_at',
  'retired_at',
  'updated_at',
];

const clone = (value) => JSON.parse(JSON.stringify(value));

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

// Which half of the output contract a definition declares. `both` is a real state — the loader
// tolerates it, so specs like that are running right now — and create rejects it, which is why
// the duplicate has to resolve it rather than pass it through.
export const outputContractOf = (definition) => {
  const source = isObject(definition) ? definition : {};
  const output = isObject(source.output_extraction) ? source.output_extraction : {};
  const gen = isObject(source.score_gen) ? source.score_gen : {};
  const schema = output.output_schema === undefined || output.output_schema === null ? null : output.output_schema;
  const prose = typeof gen.format_prompt === 'string' && gen.format_prompt.trim() !== '' ? gen.format_prompt : null;

  if (schema && prose) return { kind: 'both', schema, prose };
  if (schema) return { kind: 'schema', schema, prose: null };
  if (prose) return { kind: 'prose', schema: null, prose };
  return { kind: 'none', schema: null, prose: null };
};

// A source with no contract at all cannot be duplicated here: create 422s on it, and the fix is a
// decision about the output shape, which is exactly the decision this form refuses to let anyone
// make. Undefined spec_json means the list row did not carry one — unknown, not broken.
export const canDuplicate = (definition) =>
  definition === undefined || definition === null ? true : outputContractOf(definition).kind !== 'none';

// The fields the model's answer must still contain after the prompt changes. Read from wherever
// the source keeps them — desugared `scores[]`, the legacy `score_jsonpath`, or `extra_columns` —
// because this list is the one thing on the read-only rail the new prompt can actually break.
export const outputFieldsOf = (definition) => {
  const source = isObject(definition) ? definition : {};
  const output = isObject(source.output_extraction) ? source.output_extraction : {};
  const fields = [];

  if (Array.isArray(output.scores)) {
    output.scores.forEach((score) => {
      if (isObject(score)) fields.push({ name: score.name, jsonpath: score.jsonpath, kind: 'score' });
    });
  }
  if (typeof output.score_jsonpath === 'string') {
    fields.push({ name: 'score', jsonpath: output.score_jsonpath, kind: 'score' });
  }
  if (isObject(output.extra_columns)) {
    Object.keys(output.extra_columns).forEach((name) => {
      fields.push({ name, jsonpath: output.extra_columns[name], kind: 'extra' });
    });
  }
  return fields;
};

// The documents kind decides the Jinja namespace, and the form does not change it — so the whole
// variable rail resolves once, when the source is picked.
export const documentsKindOf = (definition) =>
  ((isObject(definition) ? definition : {}).document_set || {}).documents?.kind;

// `original` is record.spec_json from GET /score_specs/:id. Returns the POST body, a
// plain-language list of what was rewritten, and a `blocked` reason when the source cannot be
// duplicated at all (in which case `spec` is null).
//
// `outputOverride` / `formatPromptOverride` carry the operator's contract edits. They land LAST, on
// top of the rewrites, and they add nothing to `changes` — that list keeps describing what was done
// to the SOURCE, and the contract diff is reported separately by `contractChanges`. Two things
// cannot be merged into one list without one of them lying about the other.
export const buildDuplicate = (
  original,
  { stem, version, prompt, description, outputOverride, formatPromptOverride }
) => {
  if (!isObject(original)) {
    return { spec: null, changes: [], blocked: 'No source spec loaded.' };
  }

  const contract = outputContractOf(original);
  if (contract.kind === 'none') {
    return {
      spec: null,
      changes: [],
      blocked:
        'This spec declares no output contract — neither output_schema nor format_prompt — so a copy of it would be rejected on create. It has to be fixed in the advanced editor first.',
    };
  }

  const spec = clone(original);
  const changes = [];

  OPERATIONAL_KEYS.forEach((key) => delete spec[key]);

  if ('enabled_in' in spec) {
    delete spec.enabled_in;
    changes.push('Removed "enabled_in" — retired; the environment is whichever store you write to.');
  }

  const gen = isObject(spec.score_gen) ? spec.score_gen : {};
  spec.score_gen = gen;
  if (gen.parallelize !== undefined) {
    delete gen.parallelize;
    changes.push('Removed "score_gen.parallelize" — retired.');
  }

  const output = isObject(spec.output_extraction) ? spec.output_extraction : {};
  spec.output_extraction = output;
  if (output.drop_zero_scores !== undefined) {
    delete output.drop_zero_scores;
    changes.push('Removed "output_extraction.drop_zero_scores" — retired.');
  }
  if (typeof output.score_jsonpath === 'string') {
    output.scores = [{ name: 'score', jsonpath: output.score_jsonpath }];
    delete output.score_jsonpath;
    changes.push(`Rewrote "score_jsonpath" as scores: [{ name: "score", jsonpath: "${output.scores[0].jsonpath}" }].`);
  }

  if (contract.kind === 'both') {
    delete gen.format_prompt;
    changes.push('Dropped "score_gen.format_prompt" — output_schema is the contract the engine enforces.');
  }

  // A different prompt is a different signal, so it cannot publish the source's feed. This one
  // passes create and lock and only fails at approve, on a spec that can no longer be edited.
  (Array.isArray(output.scores) ? output.scores : []).forEach((score) => {
    if (isObject(score) && score.feed_name) {
      changes.push(`Cleared feed_name "${score.feed_name}" on score "${score.name}" — a copy does not inherit a feed.`);
      delete score.feed_name;
    }
  });

  spec.score_spec_id = `${stem}_v${version}`;
  // Always derived alongside the id. Create checks the id pattern but NOT that version agrees
  // with it; that runs at lock, on a spec that is frozen by then.
  spec.version = version;
  spec.score_gen = { ...gen, prompt };
  spec.description = description;
  spec.notes = [`Duplicated from ${original.score_spec_id} with a revised prompt.`, spec.notes]
    .filter(Boolean)
    .join('\n\n');

  // --- the operator's contract edits, on top of everything above ---
  if (isObject(outputOverride)) {
    const merged = { ...spec.output_extraction, ...outputOverride };
    // An empty map is not a declaration of no columns, it is noise on the wire.
    if (isObject(merged.extra_columns) && Object.keys(merged.extra_columns).length === 0) {
      delete merged.extra_columns;
    }
    spec.output_extraction = merged;
  }

  // Guarded, not trusted: writing a prose contract onto a spec that also declares an output_schema
  // is the two-contracts 422, and the caller is a form that can be looking at either kind.
  if (typeof formatPromptOverride === 'string' && spec.output_extraction.output_schema == null) {
    spec.score_gen = { ...spec.score_gen, format_prompt: formatPromptOverride };
  }

  // A schema arriving on a spec that declared prose is a CONVERSION, and the prose has to go with it —
  // declaring both is a 422. The source-side resolution above cannot see this: it ran before the
  // override existed.
  if (spec.output_extraction.output_schema != null && spec.score_gen.format_prompt !== undefined) {
    const gen = { ...spec.score_gen };
    delete gen.format_prompt;
    spec.score_gen = gen;
  }

  return { spec, changes, blocked: null };
};
